import * as React from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import styled from 'styled-components'
import CloudinaryImage from 'web-makeup/dist/CloudinaryImage'
import Dropdown from 'web-makeup/dist/Dropdown'
import notification from 'web-makeup/dist/Notification'
import Icon, { IconType } from 'web-makeup/dist/Icon'
import Menu from 'web-makeup/dist/Menu'
import { logout } from 'web-fns/dist/auth/auth'

import EnvironmentContext from '../EnvironmentContext'
import { UserContext } from '../UserProvider/UserProvider'

const MenuItem = Menu.Item
const MenuDivider = Menu.Divider

const UserInfo = styled.div`
  display: grid;
  grid-template-columns: 2rem auto;
  cursor: pointer;
  align-items: center;
  align-self: stretch;
  justify-content: flex-end;
`

const StyledMenu = styled(Menu as any)`
  && {
    min-width: 15.5rem;
    background-color: #3e3c3c;
    color: white;
    padding: 0;

    .ant-dropdown-menu {
      border-radius: 0;
    }

    > .ant-dropdown-menu-item {
      padding: 1rem;
      background-color: #3e3c3c;

      > a {
        color: white;
      }

      &:hover {
        background-color: #5a5a5a;
      }
    }

    > .ant-dropdown-menu-item-divider {
      background-color: #212124;
    }
  }
`

const StyledCloudinaryImage = styled(CloudinaryImage)`
  display: block;
  overflow: hidden;
  border: 1px solid white;
  border-radius: 100%;
  width: 2rem;
  height: 2rem;
  line-height: 1;

  img {
    width: 2rem;
    height: 2rem;
  }
`

const SmallStyledCloudinaryImage = styled(StyledCloudinaryImage as any)`
  width: 1.25rem;
  height: 1.25rem;

  img {
    width: 1.25rem;
    height: 1.25rem;
  }
`

const DisabledMenuItem = styled(MenuItem)`
  &&& {
    background-color: #3c3c3c;
    padding: 0.625rem 1rem;
    &:hover {
      background-color: #3c3c3c;
    }
  }
`

const BoldStyledH5 = styled.h5`
  font-size: 0.875rem;
  color: white;
  font-weight: 900;
`

const StyledH5 = styled.h5`
  font-size: 0.875rem;
  color: white;
`

const StatusButton = styled.button`
  color: white;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0;

  > :first-child {
    margin-right: 1.25rem;
  }
`

const StyledA = styled.a`
  && {
    display: flex;
    color: #484848;
    align-items: center;
    padding: 0;
    margin: 0;

    & > *:first-child {
      margin-right: 1rem;
    }

    @supports (display: grid) {
      display: grid;
      grid-template-columns: 1rem 1fr;
      grid-gap: 1.25rem;

      & > *:first-child {
        margin-right: 0;
      }
    }
  }

  > i {
    font-size: 1.25rem;
    color: #d1d1d1;
    font-weight: 600;
  }
`

const StyledIcon = styled(Icon)`
  fill: white;
`

const StyledMenuItem = styled(MenuItem)`
  &&& {
    padding: 1.125rem;
    height: auto;
    background: ${(props: { background?: string }) => props.background || '#3e3c3c'};
  }
`

const StyledDivider = styled(MenuDivider)`
  &&& {
    background: #505050;
    margin: 0;
  }
`

interface OwnProps {
  avatar: any
  projectsCount?: number | null
  brandName?: string | null
  brandUuid?: string | null
}

export const logoutAndRedirect = async intl => {
  try {
    await fetch(`${window.env.GEN1_BASE_URL}/sign_out`, {
      credentials: 'include',
      method: 'DELETE',
    })
    logout()
    window.location.assign(window.env.GEN1_BASE_URL)
  } catch {
    notification.error({
      message: intl.formatMessage({
        id: 'logout.error.text',
      }),
      description: null,
    })
  }
}

const UserDropdown: React.FC<OwnProps & ReactIntl.InjectedIntlProps> = ({
  avatar,
  intl,
  projectsCount,
  brandName,
  brandUuid,
}) => {
  const { GEN1_BASE_URL } = React.useContext(EnvironmentContext)
  const { authenticated, getCurrentUser } = React.useContext(UserContext)
  return (
    <Dropdown
      data-testid="user-dropdown"
      placement="bottomRight"
      overlay={
        <StyledMenu>
          <DisabledMenuItem>
            {brandName && <BoldStyledH5>{brandName}</BoldStyledH5>}
            <StyledH5>
              <FormattedMessage id="user_dropdown.active_projects.text" /> {projectsCount}
            </StyledH5>
          </DisabledMenuItem>
          <StyledDivider />
          <StyledMenuItem>
            <StyledA
              data-testid="user-dropdown-link-account"
              href={`${GEN1_BASE_URL}/organisations/${brandUuid}/team`}
            >
              <StyledIcon type={'team' as IconType} />
              <FormattedMessage id="user_dropdown.team.text" />
            </StyledA>
          </StyledMenuItem>
          <StyledDivider />
          <StyledMenuItem>
            <StyledA
              data-testid="user-dropdown-link-account"
              href={`${GEN1_BASE_URL}/organisations/${brandUuid}/connections`}
            >
              <StyledIcon type={'integration' as IconType} />
              <FormattedMessage id="user_dropdown.integrations.text" />
            </StyledA>
          </StyledMenuItem>
          <StyledDivider />
          <StyledMenuItem>
            <StyledA
              data-testid="user-dropdown-link-account"
              href={`${GEN1_BASE_URL}/organisations/${brandUuid}/details`}
            >
              <StyledIcon type={'information' as IconType} />
              <FormattedMessage id="user_dropdown.brand_details.text" />
            </StyledA>
          </StyledMenuItem>
          <StyledDivider />
          <StyledMenuItem>
            <StyledA
              data-testid="user-dropdown-link-account"
              href={`${GEN1_BASE_URL}/organisations/${brandUuid}/subbrands`}
            >
              <StyledIcon type={'subbrand' as IconType} />
              <FormattedMessage id="user_dropdown.sub_brands.text" />
            </StyledA>
          </StyledMenuItem>
          <StyledDivider />
          <StyledMenuItem>
            <StyledA
              data-testid="user-dropdown-link-account"
              href={`${GEN1_BASE_URL}/organisations/${brandUuid}/account`}
            >
              <StyledIcon type={'payment' as IconType} />
              <FormattedMessage id="user_dropdown.payments.text" />
            </StyledA>
          </StyledMenuItem>
          <StyledDivider />
          <StyledMenuItem background="#4a4a4a">
            {authenticated && (
              <StyledA
                data-testid="user-dropdown-link-account"
                href={`${GEN1_BASE_URL}/humans/${getCurrentUser('id')}`}
              >
                <SmallStyledCloudinaryImage
                  source={{
                    id:
                      avatar && avatar.cloudinaryId
                        ? avatar.cloudinaryId
                        : 'placeholders/icon-user',
                    type: avatar && avatar.cloudinaryType ? avatar.cloudinaryType : 'upload',
                  }}
                  sizes={{
                    mobile: {
                      width: 20,
                      height: 20,
                    },
                    desktop: {
                      width: 20,
                      height: 20,
                    },
                  }}
                  rounded
                />
                <FormattedMessage id="user_dropdown.edit_profile.text" />
              </StyledA>
            )}
          </StyledMenuItem>
          <StyledDivider />
          <StyledMenuItem
            background="#4a4a4a"
            data-testid="user-dropdown-logout"
            onClick={() => logoutAndRedirect(intl)}
          >
            <StatusButton>
              <CloudinaryImage
                source={{
                  id: 'icons/producers/logout',
                  type: 'upload',
                }}
                sizes={{
                  mobile: {
                    width: 24,
                    height: 24,
                  },
                  desktop: {
                    width: 24,
                    height: 24,
                  },
                }}
              />
              <FormattedMessage id="user_dropdown.logout.text" />
            </StatusButton>
          </StyledMenuItem>
        </StyledMenu>
      }
    >
      <UserInfo>
        <StyledCloudinaryImage
          source={{
            id: avatar && avatar.cloudinaryId ? avatar.cloudinaryId : 'placeholders/icon-user',
            type: avatar && avatar.cloudinaryType ? avatar.cloudinaryType : 'upload',
          }}
          sizes={{
            mobile: {
              width: 32,
              height: 32,
            },
            desktop: {
              width: 32,
              height: 32,
            },
          }}
          rounded
        />
      </UserInfo>
    </Dropdown>
  )
}

export default injectIntl(UserDropdown)



// WEBPACK FOOTER //
// ./src/components/UserDropDown/UserDropDown.tsx